import { Loader2 } from 'lucide-react';
import type { ChatListingAttachment, ChatMessage, ChatMessageAttachment } from '@/entities/chats';
import { formatShortDate, useI18n } from '@/shared/i18n';
import { splitMessageByListings } from '@/shared/lib/listing-anchors';
import { ListingCard } from '@/shared/ui/listing-card';
import { Avatar, AvatarFallback } from '@/shared/ui/shadcn/avatar';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Bubble, BubbleContent } from '@/shared/ui/shadcn/bubble';
import { Marker, MarkerContent } from '@/shared/ui/shadcn/marker';
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
  MessageHeader,
} from '@/shared/ui/shadcn/message';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from '@/shared/ui/shadcn/message-scroller';

function systemKind(attachments?: ChatMessageAttachment[]) {
  const marker = (attachments ?? []).find((item) => item?.type === 'system');
  const kind = marker && 'kind' in marker ? marker.kind : undefined;
  return typeof kind === 'string' ? kind : 'info';
}

function senderName(sender: string, labels: { client: string; agata: string; operator: string }) {
  if (sender === 'interlocutor') return labels.client;
  if (sender === 'agent') return labels.agata;
  if (sender === 'user') return labels.operator;
  return sender;
}

function MessageBody({
  content,
  attachments,
}: {
  content: string;
  attachments?: ChatMessageAttachment[];
}) {
  const listings = (attachments ?? []).filter(
    (item): item is ChatListingAttachment =>
      item?.type === 'listing' && typeof item.id === 'string' && item.id.length > 0,
  );
  const segments = splitMessageByListings(content, listings);
  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      {segments.map((segment, index) =>
        segment.kind === 'image' ? (
          <a
            key={`image-${index}`}
            href={segment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block max-w-[300px]"
          >
            <img
              src={segment.url}
              alt=""
              loading="lazy"
              className="w-full rounded-xl object-cover"
            />
          </a>
        ) : segment.kind === 'listing' ? (
          <ListingCard
            key={`listing-${segment.listing.id}`}
            title={segment.listing.title}
            url={segment.listing.url}
            photos={segment.listing.photos}
            priceThb={
              segment.listing.price_thb != null ? String(segment.listing.price_thb) : undefined
            }
            usdPrice={
              segment.listing.usd_price != null ? String(segment.listing.usd_price) : undefined
            }
            priceQuote={
              segment.listing.price_quote != null ? String(segment.listing.price_quote) : undefined
            }
            quoteCurrency={segment.listing.quote_currency}
            district={segment.listing.district}
            areaSqm={
              segment.listing.area_sqm != null ? String(segment.listing.area_sqm) : undefined
            }
            propertyType={segment.listing.property_type}
          />
        ) : (
          <span key={`text-${index}`} className="min-w-0 break-words whitespace-pre-wrap">
            {segment.text}
          </span>
        ),
      )}
    </div>
  );
}

function SystemLine({ message }: { message: ChatMessage }) {
  const { t } = useI18n();
  const kind = systemKind(message.attachments);
  const kindLabel =
    kind === 'crm_shadow'
      ? t.systemKind.crm
      : kind === 'reminder'
        ? t.systemKind.reminder
        : kind === 'routing'
          ? t.systemKind.routing
          : t.systemKind.info;
  return (
    <div className="mx-auto flex w-full max-w-[85%] items-start gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground">
      <Badge
        variant="outline"
        className="shrink-0 border-amber-500/50 font-normal text-amber-700 dark:text-amber-300"
      >
        {kindLabel}
      </Badge>
      <span className="min-w-0 whitespace-pre-wrap break-words">{message.content}</span>
    </div>
  );
}

export function ClientChatThread({
  messages,
  loading,
}: {
  messages: ChatMessage[];
  loading: boolean;
}) {
  const { t, locale } = useI18n();
  return (
    <div className="relative min-h-0 flex-1">
      <MessageScrollerProvider autoScroll>
        <MessageScroller className="absolute inset-0 min-h-0">
          <MessageScrollerViewport>
            <MessageScrollerContent className="gap-4 p-4 pb-6">
              {loading ? (
                <Marker className="py-8">
                  <Loader2 className="size-4 animate-spin" />
                  <MarkerContent>{t.chats.loading}</MarkerContent>
                </Marker>
              ) : null}
              {!loading && messages.length === 0 ? (
                <Marker variant="separator" className="py-8">
                  <MarkerContent>{t.chats.noMessages}</MarkerContent>
                </Marker>
              ) : null}
              {messages.map((message) => {
                if (message.sender === 'system') {
                  return (
                    <MessageScrollerItem key={message.id} messageId={message.id}>
                      <SystemLine message={message} />
                    </MessageScrollerItem>
                  );
                }
                const fromClient = message.sender === 'interlocutor';
                const align = fromClient ? 'start' : 'end';
                return (
                  <MessageScrollerItem key={message.id} messageId={message.id}>
                    <Message align={align}>
                      <MessageAvatar>
                        <Avatar className="size-8">
                          <AvatarFallback className="text-[10px]">
                            {fromClient ? t.common.client.slice(0, 2) : t.common.ai}
                          </AvatarFallback>
                        </Avatar>
                      </MessageAvatar>
                      <MessageContent>
                        <div
                          className={
                            fromClient
                              ? 'flex w-max max-w-[80%] flex-col gap-2.5'
                              : 'ml-auto flex w-max max-w-[80%] flex-col items-end gap-2.5'
                          }
                        >
                          <MessageHeader>
                            {senderName(message.sender, {
                              client: t.common.client,
                              agata: t.common.agata,
                              operator: t.common.operator,
                            })}
                          </MessageHeader>
                          <Bubble align={align} variant={fromClient ? 'muted' : 'default'}>
                            <BubbleContent>
                              <MessageBody
                                content={message.content}
                                attachments={message.attachments}
                              />
                            </BubbleContent>
                          </Bubble>
                          <MessageFooter>
                            {formatShortDate(message.created_at, locale)}
                          </MessageFooter>
                        </div>
                      </MessageContent>
                    </Message>
                  </MessageScrollerItem>
                );
              })}
            </MessageScrollerContent>
          </MessageScrollerViewport>
          <MessageScrollerButton />
        </MessageScroller>
      </MessageScrollerProvider>
    </div>
  );
}
